import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChampAntiRobot from "../components/ChampAntiRobot";
import { attributionCourante } from "../utils/attribution";
import { serviceJsonLd, faqJsonLd, filArianeJsonLd } from "../utils/donneesStructurees";
import { Button } from "../components/ui/button";
import {
  Search, FileText, Eye, HandHelping, CheckCircle, ShieldCheck,
  Phone, ArrowRight, MapPin, Building2, Users, Receipt,
} from "lucide-react";

/* ------------------------------------------------------------------ *
 * Dispositifs présentés — RÉELS, repris des données déjà vérifiées du
 * site (page /cumul-aides-ma-prime-adapt-idf). Rien n'est inventé.
 * Volontairement exclus : le crédit d'impôt autonomie de 25 %, supprimé
 * pour les dépenses payées à compter du 1er janvier 2026 ; les aides
 * énergie (hors périmètre adaptation de TEF).
 * Présentés comme POTENTIELS : aucune promesse d'obtention.
 * ------------------------------------------------------------------ */
const DISPOSITIFS = [
  {
    icone: Building2,
    nom: "Aides des départements et des communes",
    description:
      "De nombreuses collectivités versent une aide à l'adaptation ou à l'amélioration du logement, qui s'ajoute aux aides nationales.",
    projet: "Selon votre commune et votre département",
    source: "Conseils départementaux et communes",
  },
  {
    icone: Users,
    nom: "Caisses de retraite",
    description:
      "CARSAT, Agirc-Arrco, MSA ou caisses des fonctions publiques peuvent participer au financement, selon votre caisse et votre situation.",
    projet: "Pour les retraités du régime concerné",
    source: "Votre caisse de retraite",
  },
  {
    icone: HandHelping,
    nom: "APA — volet aménagement du logement",
    description:
      "L'Allocation personnalisée d'autonomie comporte un volet qui peut financer des équipements d'adaptation, selon le degré de perte d'autonomie (GIR).",
    projet: "Perte d'autonomie liée à l'âge",
    source: "Conseil départemental",
  },
  {
    icone: HandHelping,
    nom: "PCH — prestation de compensation du handicap",
    description:
      "Elle peut prendre en charge une partie des aménagements du logement liés à une situation de handicap.",
    projet: "Situation de handicap reconnue",
    source: "MDPH",
  },
  {
    icone: Receipt,
    nom: "TVA à taux réduit (5,5 %)",
    description:
      "Certains travaux bénéficient d'une TVA réduite. Ce n'est pas une aide à demander : elle s'applique directement sur la facture de l'artisan, à condition qu'il l'applique correctement.",
    projet: "Travaux éligibles réalisés par un professionnel",
    source: "Service des impôts / artisan",
  },
];

const ETAPES = [
  { n: "1", icone: FileText, titre: "Vous nous présentez votre projet",
    texte: "Vous nous transmettez les informations principales : votre projet, votre logement et votre situation." },
  { n: "2", icone: Search, titre: "Nous étudions les aides potentielles",
    texte: "Nous identifions les dispositifs susceptibles de correspondre à votre projet, selon les critères de chaque organisme." },
  { n: "3", icone: Eye, titre: "Vous avez une vision claire",
    texte: "Nous vous présentons les aides identifiées. Une estimation n'est ni une éligibilité confirmée, ni une aide obtenue." },
  { n: "4", icone: HandHelping, titre: "Nous vous accompagnons dans les démarches",
    texte: "Pour les dispositifs entrant dans notre périmètre, nous vous aidons à constituer et suivre vos demandes." },
];

const FAQ = [
  {
    question: "Qu'est-ce qu'une aide complémentaire pour des travaux ?",
    reponse:
      "C'est un dispositif de financement qui peut venir s'ajouter à une aide principale pour réduire le reste à charge de vos travaux. Selon votre situation, plusieurs aides peuvent parfois se compléter.",
  },
  {
    question: "Quelles aides peuvent compléter le financement de mes travaux ?",
    reponse:
      "Cela dépend de votre projet, de votre logement, de votre localisation et de votre situation. Il peut s'agir d'aides de votre département ou de votre commune, de votre caisse de retraite, de l'APA ou de la PCH sur leur volet aménagement, ou encore d'une TVA à taux réduit sur les travaux.",
  },
  {
    question: "Comment savoir à quelles aides je peux prétendre ?",
    reponse:
      "Chaque dispositif a ses propres critères, et il est difficile de tous les connaître. C'est précisément l'objet de notre étude : à partir des informations sur votre projet, nous identifions les dispositifs susceptibles de correspondre à votre situation.",
  },
  {
    question: "Peut-on cumuler plusieurs aides pour des travaux ?",
    reponse:
      "Souvent oui, mais chaque dispositif a ses propres règles de cumul. En général, une même dépense n'est pas financée deux fois, et certaines aides locales peuvent s'exclure entre elles. On étudie alors, selon votre situation, la combinaison la plus favorable.",
  },
  {
    question: "Le montant des aides est-il garanti ?",
    reponse:
      "Non. Une estimation n'est jamais une garantie d'obtention. Chaque organisme reste seul décisionnaire, selon ses propres règles et selon votre situation. Méfiez-vous d'un interlocuteur qui vous promet un montant dès le premier appel.",
  },
  {
    question: "Quelles aides une caisse de retraite peut-elle verser pour adapter un logement ?",
    reponse:
      "Les caisses de retraite — CARSAT, Agirc-Arrco, MSA ou caisses des fonctions publiques — peuvent participer au financement de travaux d'adaptation du logement, par exemple l'aménagement d'une salle de bain. Le montant et les conditions dépendent de votre caisse et de votre situation ; la demande se fait directement auprès d'elle.",
  },
  {
    question: "Existe-t-il des aides départementales ou communales pour adapter son logement ?",
    reponse:
      "Oui. De nombreux départements et communes versent une aide à l'adaptation ou à l'amélioration du logement, qui s'ajoute aux aides nationales. Ces aides locales varient fortement d'un territoire à l'autre : c'est l'un des points que nous vérifions pour votre projet, selon votre code postal.",
  },
  {
    question: "Quelles aides peuvent réduire le reste à charge après MaPrimeAdapt' ?",
    reponse:
      "Après MaPrimeAdapt', il reste souvent une part à votre charge. Selon votre situation, des aides de votre caisse de retraite, de votre département ou de votre commune peuvent venir la réduire. Rechercher ces aides complémentaires est précisément l'objet de notre accompagnement, distinct de notre mission d'AMO MaPrimeAdapt'.",
  },
  {
    question: "Faut-il être bénéficiaire de MaPrimeAdapt' pour rechercher des aides complémentaires ?",
    reponse:
      "Non. La recherche d'aides complémentaires est une démarche distincte de notre mission d'Assistant à Maîtrise d'Ouvrage MaPrimeAdapt'. Elle est ouverte indépendamment de tout dossier MaPrimeAdapt'.",
  },
];

const NATURES = [
  "Salle de bain / douche", "Monte-escalier", "Accessibilité du logement",
  "Aménagement extérieur", "Autre adaptation du logement", "Autres travaux",
];

const VIDE = { nom: "", prenom: "", telephone: "", email: "", codePostal: "",
  natureProjet: "", message: "", consent: false, website: "" };

/* ------------------------- Formulaire dédié ------------------------- */
const FormulaireAides = () => {
  const [v, setV] = useState(VIDE);
  const [envoi, setEnvoi] = useState(false);
  const [retour, setRetour] = useState(null);
  const champ = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));

  const soumettre = async (e) => {
    e.preventDefault();
    if (!v.consent) { setRetour({ type: "erreur", texte: "Merci d'accepter le traitement de vos données pour être recontacté." }); return; }
    setRetour(null); setEnvoi(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-aides-complementaires`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: v.nom.trim(), prenom: v.prenom.trim(), telephone: v.telephone.trim(),
          email: v.email.trim(), code_postal: v.codePostal.trim(),
          nature_projet: v.natureProjet, message: v.message.trim(),
          website: v.website, attribution: attributionCourante(),
        }),
      });
      const d = await r.json();
      if (d.success) { setV(VIDE); setRetour({ type: "succes", texte: d.message }); }
      else setRetour({ type: "erreur", texte: d.message || "L'envoi n'a pas abouti." });
    } catch {
      setRetour({ type: "erreur", texte: "L'envoi n'a pas abouti. Réessayez, ou appelez-nous au 09 39 03 54 44." });
    } finally { setEnvoi(false); }
  };

  const champClasse = "w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-200";
  const Label = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-gray-700">{children}</label>
  );

  return (
    <form onSubmit={soumettre} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label htmlFor="ac-prenom">Prénom</Label>
          <input id="ac-prenom" value={v.prenom} onChange={champ("prenom")} className={champClasse} /></div>
        <div><Label htmlFor="ac-nom">Nom</Label>
          <input id="ac-nom" required value={v.nom} onChange={champ("nom")} className={champClasse} /></div>
        <div><Label htmlFor="ac-tel">Téléphone</Label>
          <input id="ac-tel" required type="tel" value={v.telephone} onChange={champ("telephone")} className={champClasse} /></div>
        <div><Label htmlFor="ac-email">E-mail</Label>
          <input id="ac-email" required type="email" value={v.email} onChange={champ("email")} className={champClasse} /></div>
        <div><Label htmlFor="ac-cp">Code postal</Label>
          <input id="ac-cp" inputMode="numeric" pattern="[0-9]{5}" maxLength={5} value={v.codePostal} onChange={champ("codePostal")} placeholder="75010" className={champClasse} /></div>
        <div><Label htmlFor="ac-projet">Nature du projet</Label>
          <select id="ac-projet" value={v.natureProjet} onChange={champ("natureProjet")} className={champClasse}>
            <option value="">Choisir…</option>
            {NATURES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select></div>
        <div className="sm:col-span-2"><Label htmlFor="ac-msg">Informations complémentaires (facultatif)</Label>
          <textarea id="ac-msg" rows={3} value={v.message} onChange={champ("message")} className={champClasse} /></div>
      </div>

      <ChampAntiRobot id="website-aides-complementaires" valeur={v.website}
        onChange={(e) => setV((s) => ({ ...s, website: e.target.value }))} />

      <label className="mt-4 flex items-start gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={v.consent} onChange={(e) => setV((s) => ({ ...s, consent: e.target.checked }))} className="mt-1" />
        <span>J'accepte que mes données soient utilisées pour étudier ma demande et être recontacté. Elles ne sont pas transmises à des tiers.</span>
      </label>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xs text-xs text-gray-500">Réponse sous 48 h ouvrées. Sans engagement.</p>
        <Button type="submit" disabled={envoi} className="bg-orange-700 hover:bg-orange-800">
          {envoi ? "Envoi…" : "Étudier les aides pour mon projet"}
        </Button>
      </div>

      {retour && (
        <p role="status" className={`mt-4 rounded-md px-4 py-3 text-sm ${
          retour.type === "succes" ? "bg-green-50 text-green-800 border border-green-200"
          : "bg-red-50 text-red-800 border border-red-200"}`}>{retour.texte}</p>
      )}
    </form>
  );
};

/* ----------------------------- Page ----------------------------- */
const AidesComplementaires = () => {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Aides complémentaires pour financer vos travaux | TEF"
        description="Votre projet de travaux peut peut-être bénéficier d'autres aides financières. Transition Énergie France recherche les dispositifs susceptibles de correspondre à votre situation et vous accompagne dans vos démarches."
        chemin="/aides-complementaires"
        donnees={[
          serviceJsonLd({
            nom: "Recherche d'aides complémentaires pour travaux",
            description:
              "Étude et recherche des dispositifs d'aide susceptibles de compléter le financement d'un projet de travaux, et accompagnement dans les démarches.",
            url: "/aides-complementaires",
          }),
          faqJsonLd(FAQ.map((f) => ({ question: f.question, reponse: f.reponse }))),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Aides complémentaires", url: "/aides-complementaires" },
          ]),
        ]}
      />

      <Header />

      <main id="contenu">
      {/* ── HERO ── */}
      <section className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-16 lg:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold leading-tight text-gray-900 lg:text-5xl">
              Aides complémentaires pour financer vos travaux
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-xl leading-relaxed text-gray-700">
              Votre projet peut peut-être bénéficier d'autres aides financières.
              Transition Énergie France recherche les dispositifs susceptibles de
              correspondre à votre situation et vous accompagne dans vos démarches.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href="#etudier"><Button className="bg-orange-700 hover:bg-orange-800 text-base px-6 py-6">
                Étudier les aides pour mon projet <ArrowRight className="ml-2 h-5 w-5" /></Button></a>
              <a href="tel:0939035444"><Button variant="outline" className="border-2 text-base px-6 py-6">
                <Phone className="mr-2 h-5 w-5" /> Être rappelé</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* ── POURQUOI ── */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-gray-900">
              Ne passez pas à côté d'une aide qui pourrait financer votre projet
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-gray-700">
              Les dispositifs de financement dépendent de nombreux critères : la
              nature des travaux, votre situation, votre logement, sa localisation,
              vos ressources lorsque le dispositif les prend en compte, et les
              conditions propres à chaque organisme.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-gray-700">
              Personne ne connaît par cœur toutes les aides financières pour des
              travaux : aides locales, aides de votre caisse de retraite, dispositifs
              départementaux ou communaux. C'est précisément l'intérêt d'une étude :
              passer en revue, pour votre situation, les aides susceptibles de
              financer votre reste à charge.
            </p>
          </div>
        </div>
      </section>

      {/* ── QUELLES AIDES ── */}
      <section className="bg-gray-50 py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Des aides différentes selon votre situation et votre projet
            </h2>
            <p className="mt-4 text-lg text-gray-700">
              Aides locales, aides des caisses de retraite, dispositifs départementaux :
              voici des exemples d'aides financières que nous étudions pour vos travaux.
              Leur présence ici ne signifie pas que chacune vous est accessible.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            {DISPOSITIFS.map((d) => {
              const Ic = d.icone;
              return (
                <div key={d.nom} className="rounded-2xl border border-gray-200 bg-white p-6">
                  <div className="mb-3 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-orange-50 text-orange-700">
                      <Ic className="h-5 w-5" aria-hidden="true" /></span>
                    <h3 className="text-lg font-semibold text-gray-900">{d.nom}</h3>
                  </div>
                  <p className="text-gray-700">{d.description}</p>
                  <dl className="mt-4 space-y-1 text-sm">
                    <div className="flex gap-2"><dt className="font-medium text-gray-500">Projet concerné :</dt><dd className="text-gray-700">{d.projet}</dd></div>
                    <div className="flex gap-2"><dt className="font-medium text-gray-500">Source :</dt><dd className="text-gray-700">{d.source}</dd></div>
                  </dl>
                </div>
              );
            })}
          </div>

          <p className="mx-auto mt-8 max-w-3xl rounded-xl border border-gray-200 bg-white p-5 text-sm leading-relaxed text-gray-600">
            Les dispositifs présentés sont donnés à titre informatif. Leur
            disponibilité, les conditions d'éligibilité et les montants dépendent
            notamment de votre situation, de votre projet et des règles propres à
            chaque organisme financeur.
          </p>
        </div>
      </section>

      {/* ── AIDES LOCALES ÎLE-DE-FRANCE ── */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-gray-900">
              Quelles aides locales pour vos travaux en Île-de-France ?
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-gray-700">
              Assistant à maîtrise d'ouvrage agréé ANAH en Île-de-France, nous
              connaissons les aides qui changent d'un territoire à l'autre. Un même
              projet de travaux d'adaptation du logement n'ouvre pas les mêmes droits
              à Paris, en Seine-et-Marne, dans l'Essonne ou dans les Hauts-de-Seine :
              chaque département, et parfois chaque commune, a ses propres dispositifs
              d'aide.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-gray-700">
              À ces aides locales s'ajoutent, selon votre situation, les aides de votre
              caisse de retraite pour vos travaux et, le cas échéant, l'APA ou la PCH.
              Pour votre code postal, nous vérifions les aides locales réellement
              mobilisables, en complément des aides nationales — afin de réduire autant
              que possible le reste à charge de votre projet.
            </p>
          </div>
        </div>
      </section>

      {/* ── COMMENT ÇA MARCHE ── */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <h2 className="mx-auto mb-12 max-w-3xl text-center text-3xl font-bold text-gray-900">
            Comment ça marche ?
          </h2>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2 lg:grid-cols-4">
            {ETAPES.map((e) => {
              const Ic = e.icone;
              return (
                <div key={e.n} className="flex flex-col gap-3 border-t-4 border-orange-600 pt-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-700 font-bold text-white">{e.n}</span>
                    <Ic className="h-6 w-6 text-orange-700" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{e.titre}</h3>
                  <p className="text-sm leading-relaxed text-gray-700">{e.texte}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TRANSPARENCE (+ tarif, séparé de l'AMO) ── */}
      <section className="bg-gray-50 py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-orange-700" aria-hidden="true" />
              <h2 className="text-3xl font-bold text-gray-900">Un accompagnement clair, sans promesse irréaliste</h2>
            </div>
            <ul className="mt-6 space-y-3">
              {[
                "Nous recherchons les aides potentiellement mobilisables pour votre projet.",
                "Chaque dispositif conserve ses propres critères.",
                "Les organismes concernés restent seuls décisionnaires.",
                "Une estimation n'est pas une garantie d'obtention.",
                "Vous disposez d'une présentation claire des aides étudiées.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" /><span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-orange-200 bg-orange-50 p-6">
              <p className="text-sm font-medium uppercase tracking-wide text-orange-700">Notre accompagnement</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                Recherche d'aides complémentaires — à partir de 299 €
              </p>
              <p className="mt-3 text-sm leading-relaxed text-gray-700">
                Cette prestation est <strong>distincte de notre mission d'Assistant à
                Maîtrise d'Ouvrage MaPrimeAdapt'</strong>, qui, elle, est prise en charge
                par l'ANAH. La recherche d'aides complémentaires est un service à part,
                que vous pouvez solliciter indépendamment d'un dossier MaPrimeAdapt'.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOC AMO (séparation explicite) ── */}
      <section className="bg-white py-14">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-6">
            <h2 className="text-xl font-bold text-gray-900">Déjà accompagné par TEF pour MaPrimeAdapt' ?</h2>
            <p className="mt-3 leading-relaxed text-gray-700">
              La recherche d'aides complémentaires est une démarche distincte de notre
              mission d'Assistant à Maîtrise d'Ouvrage MaPrimeAdapt'. Selon votre projet
              et votre situation, d'autres dispositifs peuvent éventuellement être étudiés
              en complément.
            </p>
            <Link to="/maprimeadapt" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-orange-700 underline hover:text-orange-800">
              Découvrir notre accompagnement MaPrimeAdapt' <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-gray-50 py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <h2 className="mx-auto mb-10 max-w-3xl text-center text-3xl font-bold text-gray-900">Questions fréquentes</h2>
          <div className="mx-auto max-w-3xl divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white px-6">
            {FAQ.map((f) => (
              <details key={f.question} className="group py-5">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-semibold text-gray-900">
                  {f.question}
                  <span className="mt-1 flex-shrink-0 text-orange-700 transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 pr-8 leading-relaxed text-gray-700">{f.reponse}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL + FORMULAIRE ── */}
      <section id="etudier" className="bg-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <h2 className="text-3xl font-bold leading-tight text-gray-900">
                Vous avez un projet ? Vérifions les aides qui peuvent correspondre à votre situation.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-700">
                Décrivez-nous votre projet en quelques informations. Nous étudions les
                dispositifs susceptibles de le compléter et revenons vers vous.
              </p>
              <div className="mt-6 flex items-center gap-3 text-gray-700">
                <MapPin className="h-5 w-5 text-orange-700" aria-hidden="true" />
                <span>Transition Énergie France — AMO agréé ANAH, Île-de-France</span>
              </div>
              <a href="tel:0939035444" className="mt-3 inline-flex items-center gap-2 font-medium text-orange-700">
                <Phone className="h-5 w-5" aria-hidden="true" /> 09 39 03 54 44
              </a>
            </div>
            <FormulaireAides />
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default AidesComplementaires;
